import { z } from 'zod';
import { entityBase, id, organizationId } from './common.js';

/** A dental practice group (the business hierarchy above locations). Supports multi-group from v1. */

export const groupStatus = z.enum(['active', 'inactive']);
export type GroupStatus = z.infer<typeof groupStatus>;

export const dentalGroup = entityBase.extend({
  name: z.string().min(1),
  status: groupStatus,
  /** Group-level defaults/settings (JSON). */
  settings: z.record(z.string(), z.unknown()).nullable(),
});
export type DentalGroup = z.infer<typeof dentalGroup>;

export const createDentalGroupInput = z.object({
  organizationId,
  name: z.string().min(1),
  settings: z.record(z.string(), z.unknown()).nullish(),
});
export type CreateDentalGroupInput = z.infer<typeof createDentalGroupInput>;

export const updateDentalGroupInput = z.object({
  name: z.string().min(1).optional(),
  status: groupStatus.optional(),
  settings: z.record(z.string(), z.unknown()).nullish(),
});
export type UpdateDentalGroupInput = z.infer<typeof updateDentalGroupInput>;

export const location = entityBase.extend({
  groupId: id,
  name: z.string().min(1),
  addressLine1: z.string().min(1),
  addressLine2: z.string().nullable(),
  city: z.string().min(1),
  state: z.string().min(1),
  postalCode: z.string().min(1),
  timezone: z.string().min(1),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  /** Default staffing preferences for shifts at this location (JSON). */
  staffingDefaults: z.record(z.string(), z.unknown()).nullable(),
  isActive: z.boolean(),
});
export type Location = z.infer<typeof location>;

export const createLocationInput = z.object({
  organizationId,
  groupId: id,
  name: z.string().min(1),
  addressLine1: z.string().min(1),
  addressLine2: z.string().nullish(),
  city: z.string().min(1),
  state: z.string().min(1),
  postalCode: z.string().min(1),
  timezone: z.string().min(1),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  staffingDefaults: z.record(z.string(), z.unknown()).nullish(),
});
export type CreateLocationInput = z.infer<typeof createLocationInput>;

export const updateLocationInput = z.object({
  name: z.string().min(1).optional(),
  addressLine1: z.string().min(1).optional(),
  addressLine2: z.string().nullish(),
  city: z.string().min(1).optional(),
  state: z.string().min(1).optional(),
  postalCode: z.string().min(1).optional(),
  timezone: z.string().min(1).optional(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  staffingDefaults: z.record(z.string(), z.unknown()).nullish(),
  isActive: z.boolean().optional(),
});
export type UpdateLocationInput = z.infer<typeof updateLocationInput>;
